import queryString from "query-string";
import type { VoteAnalyticsQuery } from "./types";

/* URL <-> query for the Vote Analytics screen.
   -------------------------------------------------------------------------
   Filter state lives ENTIRELY in the URL: the screen is a link you can paste or
   bookmark, and browser Back walks the filter history. That is what stands in
   for a saved-screens feature, so all four filters must survive the round trip
   -- including the company and director chips, which the older
   ShareholderProposalAnalytics screen deliberately drops on the floor
   ("TODO: Remove this when we can support deep-linking").

   Tickers are their own labels, so companies need one param. Directors are a
   free-text contains match on the name rather than a set of ciks, so they need
   one param too -- a name is readable in a URL, where a cik is not.

   These are pure functions, kept out of the component so the round trip can be
   exercised without mounting React. */

export const baseQuery: VoteAnalyticsQuery = {
  yearFrom: null,
  yearTo: null,
  supportMin: null,
  supportMax: null,
  voteTypes: [],
  voteSubtypes: [],
  sectors: [],
  industries: [],
  sp500: false,
  keyword: "",
  companies: [],
  personName: "",
  order: "asc",
  // Ticker ascending. Kept in step with VoteProposalFact's own default: this is
  // the value encodeQuery treats as "default" and therefore OMITS from the URL,
  // so if the two sides disagree the screen shows the sort arrow on one column
  // while the server orders by another.
  orderBy: "ticker",
  page: 1,
  perPage: 25,
};

// Support bounds are a percentage: anything outside 0-100 is a hand-edited URL,
// not a reachable form state, so it is clamped rather than passed to the API.
// Returns null for an absent or unparseable bound, which means "no bound" --
// note 0 is a real floor and must survive, so callers test for null, not falsity.
export function clampPct(v: unknown): null | number {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.min(100, Math.max(0, n));
}

// The meeting_year window is always ordered. A hand-edited or stale URL can arrive
// reversed (?year_from=2026&year_to=2020), which would quietly return an
// empty screen; both years were asked for explicitly, so they are swapped rather
// than dropped. A half-open window is left alone -- the screen fills the missing
// side from the meeting_year list once meta arrives.
export function orderMeetingYears<T extends { yearFrom: null | number; yearTo: null | number }>(
  q: T
): T {
  const { yearFrom, yearTo } = q;
  if (yearFrom === null || yearTo === null) return q;
  if (yearFrom <= yearTo) return q;
  return { ...q, yearFrom: yearTo, yearTo: yearFrom };
}

function toArray(v: unknown): string[] {
  if (v === undefined || v === null) return [];
  return (Array.isArray(v) ? v : [v]).map(String).filter((s) => s.length > 0);
}

// Defaults are omitted so a default screen has a clean URL, and so the
// comparison in the Back-button effect is stable.
export function encodeQuery(q: VoteAnalyticsQuery): string {
  const out: Record<string, unknown> = {};
  if (q.yearFrom !== null) out.year_from = q.yearFrom;
  if (q.yearTo !== null) out.year_to = q.yearTo;
  if (q.supportMin !== null) out.support_min = q.supportMin;
  if (q.supportMax !== null) out.support_max = q.supportMax;
  if (q.voteTypes.length) out.vote_type = q.voteTypes;
  if (q.voteSubtypes.length) out.vote_subtype = q.voteSubtypes;
  if (q.sectors.length) out.sector = q.sectors;
  if (q.industries.length) out.industry = q.industries;
  // Only ever present when on: an absent param and sp500=false mean the same
  // thing, and leaving it out keeps the default screen's URL clean.
  if (q.sp500) out.sp500 = true;
  if (q.keyword) out.keyword = q.keyword;
  if (q.companies.length) out.ticker = q.companies.map((c) => c.ticker);
  if (q.personName) out.person_name = q.personName;
  if (q.orderBy !== baseQuery.orderBy) out.order_by = q.orderBy;
  if (q.order !== baseQuery.order) out.order = q.order;
  if (q.page !== 1) out.page = q.page;
  if (q.perPage !== baseQuery.perPage) out.per_page = q.perPage;
  return queryString.stringify(out);
}

export function decodeQuery(search: string): VoteAnalyticsQuery {
  const p = queryString.parse(search) as Record<string, unknown>;
  const tickers: string[] = toArray(p.ticker);

  const page = Number(p.page);
  const perPage = Number(p.per_page);

  return orderMeetingYears({
    ...baseQuery,
    yearFrom: p.year_from ? Number(p.year_from) : null,
    yearTo: p.year_to ? Number(p.year_to) : null,
    supportMin: clampPct(p.support_min),
    supportMax: clampPct(p.support_max),
    voteTypes: toArray(p.vote_type),
    voteSubtypes: toArray(p.vote_subtype),
    sectors: toArray(p.sector),
    industries: toArray(p.industry),
    sp500: p.sp500 === "true" || p.sp500 === true,
    keyword: p.keyword ? String(p.keyword) : "",
    companies: tickers.map((t) => ({ ticker: t, name: t })),
    personName: p.person_name ? String(p.person_name) : "",
    order: p.order === "desc" ? "desc" : "asc",
    orderBy: p.order_by ? String(p.order_by) : baseQuery.orderBy,
    page: Number.isFinite(page) && page > 0 ? Math.floor(page) : 1,
    perPage:
      Number.isFinite(perPage) && perPage > 0
        ? Math.floor(perPage)
        : baseQuery.perPage,
  });
}
